import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import { accessorialSchema } from "~/shared/schemas/accessorial-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { StopName } from "~/shared/types/stop-types.ts";
import { toCamelCase } from "~/shared/utils/string-fns.ts";

export type GroupedAccessorials = {
	locationType: Record<StopName, Accessorial[]>;
	selectable: Record<StopName, Accessorial[]>;
};

export type ShipmentAccessorialsData = {
	allAccessorials: Accessorial[];
	grouped: GroupedAccessorials;
};

const emptyGrouped = (): GroupedAccessorials => ({
	locationType: { pickup: [], delivery: [] },
	selectable: { pickup: [], delivery: [] },
});

const getStopKey = (accessorial: Accessorial): StopName | null => {
	const stop = accessorial.stopType?.name?.toLowerCase();
	if (stop === "pick") {
		return "pickup";
	}
	if (stop === "drop") {
		return "delivery";
	}
	// Accessorials without a pick/drop stop type aren't stop-specific and must
	// not be shown under a stop — mirrors quote's grouping (which buckets these
	// separately). Dumping them into delivery caused duplicate location types.
	return null;
};

/**
 * Groups the flat accessorial list by usage type (Location Type vs Selectable)
 * and stop (pickup/delivery) — mirroring how quote details shapes this data.
 */
const groupAccessorials = (list: Accessorial[]): GroupedAccessorials => {
	const grouped = emptyGrouped();

	for (const accessorial of list) {
		const key = toCamelCase(accessorial.accessorialUsageType?.name ?? "");
		const stopKey = getStopKey(accessorial);
		if (stopKey && (key === "locationType" || key === "selectable")) {
			grouped[key][stopKey].push(accessorial);
		}
	}

	return grouped;
};

/**
 * Fetches the shared accessorials list (`create-quote/accessorials`) and groups
 * it into location-type radio options and selectable accessorial checkboxes per
 * stop. This is the same source quote details uses, without the quote context.
 */
export const useGetAccessorials = (): UseQueryResult<
	ShipmentAccessorialsData,
	Error
> =>
	useQuery<ShipmentAccessorialsData>({
		queryKey: ["accessorials", "grouped"],
		queryFn: async (): Promise<ShipmentAccessorialsData> => {
			const result = await getAndValidate(
				apiRoutes.ACCESSORIALS,
				accessorialSchema.array(),
			);
			return result.success
				? {
						allAccessorials: result.data,
						grouped: groupAccessorials(result.data),
					}
				: {
						allAccessorials: [],
						grouped: emptyGrouped(),
					};
		},
		staleTime: 60 * 60 * 1000, // 1 hour — this reference data rarely changes.
	});
